import React, { useState } from 'react';

interface Product {
  id: string;
  badge: string;
  name: string;
  tagline: string;
  description: string;
  highlights: string[];
  techStack: string[];
  metrics: { label: string; value: string }[];
  status: string;
}

const PRODUCTS: Product[] = [
  {
    id: 'gnl',
    badge: 'Flagship Healthcare SaaS',
    name: 'GNL Interpreters',
    tagline: 'Enterprise Real-Time Medical Interpretation Platform',
    description:
      'Engineered and operated by CloudIgnite. GNL Interpreters is an on-demand Video Remote Interpreting (VRI) and Over-the-Phone Interpreting (OPI) cloud platform connecting healthcare organizations with certified interpreters in 200+ languages.',
    highlights: [
      'HIPAA-Compliant WebRTC Media Pipeline & End-to-End Encryption',
      'Real-Time Intelligent Dispatch & Interpreter Matching',
      'Hospital & Provider Operations Dashboards with Live Analytics',
      'Automated Call Duration Tracking, Billing & Invoicing Systems',
    ],
    techStack: ['React', 'TypeScript', 'Python', 'WebRTC', 'PostgreSQL', 'Docker', 'AWS'],
    metrics: [
      { label: 'Languages Supported', value: '200+' },
      { label: 'Platform Availability', value: '24/7' },
      { label: 'Compliance Standard', value: 'HIPAA' },
    ],
    status: 'In Active Operation',
  },
  {
    id: 'restaurant-os',
    badge: 'Hospitality SaaS',
    name: 'RestaurantOS & CRM',
    tagline: 'AI-Powered Restaurant Cost Control & CRM',
    description:
      'A unified operational platform built for restaurants to eliminate manual spreadsheet tracking, monitor supplier waste in real time, and drive customer retention through automated AI re-engagement.',
    highlights: [
      'Automated Food & Supplier Cost Tracking Dashboards',
      'AI-Powered Menu Profitability & Waste Pattern Detection',
      'Centralized Guest Profiles & Lifetime Value Tracking',
      'Automated SMS/Email Re-Engagement Triggered by Visit Frequency',
    ],
    techStack: ['React', 'TypeScript', 'Node.js', 'PostgreSQL', 'Claude AI', 'AWS'],
    metrics: [
      { label: 'Weekly Hours Saved', value: '18+ hrs' },
      { label: 'Food Waste Cut', value: '24%' },
      { label: 'ROI Benchmark', value: '3.2x' },
    ],
    status: 'Active Client Deployment',
  },
  {
    id: 'dian-accounting',
    badge: 'FinTech & Compliance',
    name: 'DIAN Automation Engine',
    tagline: 'Automated Tax & Financial Ingestion Platform',
    description:
      'High-throughput accounting and electronic invoicing synchronization system designed to process invoices, parse raw XML/PDF documents, and reconcile ledgers automatically.',
    highlights: [
      'Automated PDF & XML Invoice Data Extraction',
      'Real-Time Tax Compliance & DIAN Electronic Invoicing Sync',
      'Automated Ledger Reconciliation & Anomaly Alerts',
    ],
    techStack: ['Python', 'FastAPI', 'PostgreSQL', 'Docker', 'AWS Lambda'],
    metrics: [
      { label: 'Invoice Parsing Speed', value: '< 1 sec' },
      { label: 'Data Accuracy', value: '99.8%' },
      { label: 'Manual Effort Cut', value: '85%' },
    ],
    status: 'In Production',
  },
];

const ProductsShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('gnl');
  const currentProduct = PRODUCTS.find((p) => p.id === activeTab) || PRODUCTS[0];

  return (
    <section id="products" className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/3 left-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-400 text-xs font-semibold uppercase tracking-widest mb-4">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            Engineered & Operated by CloudIgnite
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-white">
            We Don’t Just Build Code — <span className="text-blue-400">We Run Live SaaS Products</span>
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Proof of technical execution. Beyond consulting, CloudIgnite designs, deploys, and operates high-availability platforms handling real-world enterprise traffic.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-3 mb-10 border-b border-slate-800 pb-4">
          {PRODUCTS.map((prod) => (
            <button
              key={prod.id}
              onClick={() => setActiveTab(prod.id)}
              className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2.5 ${
                activeTab === prod.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 border border-blue-500'
                  : 'bg-slate-800/80 text-slate-400 hover:text-slate-200 hover:bg-slate-800 border border-slate-700/60'
              }`}
            >
              <span>{prod.name}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/10">{prod.badge.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* Product Detail Card */}
        <div className="bg-slate-800/60 border border-slate-700/80 rounded-3xl p-8 lg:p-10 backdrop-blur-xl shadow-2xl">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 text-xs font-semibold rounded-full border border-blue-500/30">
                  {currentProduct.badge}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-emerald-400 font-medium bg-emerald-950/60 px-3 py-1 rounded-full border border-emerald-800/50">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />
                  {currentProduct.status}
                </span>
              </div>

              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">{currentProduct.name}</h3>
                <p className="text-blue-400 font-medium text-base mb-4">{currentProduct.tagline}</p>
                <p className="text-slate-300 text-sm leading-relaxed">{currentProduct.description}</p>
              </div>

              {/* Highlights List */}
              <div className="space-y-3 pt-2">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Key Engineering Features</h4>
                <div className="grid sm:grid-cols-1 gap-2.5">
                  {currentProduct.highlights.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-slate-900/40 p-3 rounded-xl border border-slate-800">
                      <div className="w-5 h-5 bg-blue-500/20 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-blue-400 font-bold">
                        ✓
                      </div>
                      <span className="text-xs text-slate-200 font-medium leading-normal">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tech Stack Pills */}
              <div className="pt-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Architecture & Tech Stack</p>
                <div className="flex flex-wrap gap-2">
                  {currentProduct.techStack.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-slate-900 border border-slate-700 text-slate-300 text-xs font-mono font-medium rounded-lg"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Metrics & Card Column */}
            <div className="lg:col-span-5 space-y-6">
              {/* Metrics Grid */}
              <div className="bg-slate-900/80 border border-slate-700/80 rounded-2xl p-6">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Operational Metrics</h4>
                <div className="space-y-4">
                  {currentProduct.metrics.map((m) => (
                    <div key={m.label} className="flex items-center justify-between border-b border-slate-800 pb-3 last:border-0 last:pb-0">
                      <span className="text-slate-400 text-xs font-medium">{m.label}</span>
                      <span className="text-xl font-bold text-blue-400 font-mono">{m.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Special Box for GNL Platform */}
              {currentProduct.id === 'gnl' && (
                <div className="bg-gradient-to-br from-blue-900/50 to-indigo-900/40 border border-blue-500/30 rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">🏥</span>
                    <h4 className="text-sm font-bold text-white">Need a Custom Real-Time SaaS?</h4>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed mb-4">
                    GNL Interpreters proves our ability to build complex, WebRTC-based, HIPAA-compliant platforms. We can adapt this architecture for your industry.
                  </p>
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs py-2.5 px-4 rounded-xl transition-colors"
                  >
                    Discuss Platform Engineering
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsShowcase;
